import React, { useEffect, useRef } from 'react';

const SmoothScroll = ({ children }) => {
  const contentRef = useRef(null);
  const scrollYRef = useRef(0);
  const currentYRef = useRef(0);
  const isScrollingRef = useRef(false);
  const rafIdRef = useRef(null);

  useEffect(() => {
    const content = contentRef.current;
    const ease = 0.05;

    const updateScroll = () => {
      scrollYRef.current = window.pageYOffset || document.documentElement.scrollTop;
    };

    const smoothScroll = () => {
      if (!isScrollingRef.current) {
        const diff = scrollYRef.current - currentYRef.current;
        currentYRef.current += diff * ease;

        if (content) {
          content.style.transform = `translate3d(0, ${-currentYRef.current}px, 0)`;
        }
      }

      rafIdRef.current = requestAnimationFrame(smoothScroll);
    };

    rafIdRef.current = requestAnimationFrame(smoothScroll);
    window.addEventListener('scroll', updateScroll, { passive: true });

    const setBodyHeight = () => {
      if (content) {
        requestAnimationFrame(() => {
          const contentHeight = content.scrollHeight;
          document.body.style.height = `${contentHeight}px`;
        });
      }
    };

    const initialTimer = setTimeout(setBodyHeight, 0);
    window.addEventListener('resize', setBodyHeight);

    const resizeObserver = new ResizeObserver(() => {
      setBodyHeight();
    });

    if (content) {
      resizeObserver.observe(content);
    }

    const handleAnchorClick = (e) => {
      const target = e.target.closest('a');
      if (target && target.hash && target.hash.startsWith('#')) {
        e.preventDefault();
        const targetElement = document.querySelector(target.hash);
        if (targetElement) {
          // Calculate the actual offset by getting the element's position
          // and accounting for the current transform
          const rect = targetElement.getBoundingClientRect();
          const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
          const offsetTop = rect.top + currentScroll;
          
          isScrollingRef.current = true;

          const duration = 1000;
          const startY = currentScroll;
          const distance = offsetTop - startY;
          const startTime = performance.now();

          const easeInOutCubic = (t) => {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          };

          const animateScroll = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeProgress = easeInOutCubic(progress);
            const newY = startY + distance * easeProgress;

            window.scrollTo(0, newY);
            
            // Force immediate update during programmatic scroll
            const scrollPos = window.pageYOffset || document.documentElement.scrollTop;
            currentYRef.current = scrollPos;
            scrollYRef.current = scrollPos;

            if (content) {
              content.style.transform = `translate3d(0, ${-scrollPos}px, 0)`;
            }

            if (progress < 1) {
              requestAnimationFrame(animateScroll);
            } else {
              setTimeout(() => {
                isScrollingRef.current = false;
              }, 100);
            }
          };

          requestAnimationFrame(animateScroll);
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    return () => {
      window.removeEventListener('scroll', updateScroll);
      window.removeEventListener('resize', setBodyHeight);
      document.removeEventListener('click', handleAnchorClick);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (resizeObserver && content) {
        resizeObserver.unobserve(content);
      }
      clearTimeout(initialTimer);
      document.body.style.height = '';
      if (content) content.style.transform = '';
    };
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        backgroundColor: '#000',
        willChange: 'transform',
      }}
    >
      <div ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default SmoothScroll;